/** The avatar and its menu (design v2 § 7): who you are, the plan in a sentence, both meters,
 * then every place the header links to. On a phone the header's links and numbers wrap out
 * of view, and this menu is where they live. */
import { useEffect, useRef, useState } from "react";
import type { Entitlement } from "../api/api";
import { Meter } from "../billing/Meter";
import { planLine, usageMeters } from "../billing/PlanBadge";

type Props = {
  email: string;
  entitlement: Entitlement | null;
  onSubjects: () => void;
  onFeedback: () => void;
  onAccount: () => void;
  onSignOut: () => void;
};

export function AccountMenu({
  email,
  entitlement,
  onSubjects,
  onFeedback,
  onAccount,
  onSignOut,
}: Props) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const go = (f: () => void) => () => {
    setOpen(false);
    f();
  };
  const { pages, papers } = entitlement?.active
    ? usageMeters(entitlement)
    : { pages: undefined, papers: undefined };
  const initial = (email.trim()[0] ?? "?").toUpperCase();

  return (
    <div className="account-menu" ref={root}>
      <button
        type="button"
        className={`account-menu__avatar${open ? " account-menu__avatar--open" : ""}`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Account"
        onClick={() => setOpen((o) => !o)}
      >
        <span aria-hidden="true">{initial}</span>
      </button>
      {open && (
        <div className="account-menu__panel" role="menu" aria-label="Account">
          <div className="account-menu__who">
            <div className="account-menu__email">{email}</div>
            {entitlement && <div className="account-menu__plan">{planLine(entitlement)}</div>}
          </div>
          {(pages || papers) && (
            <div className="account-menu__meters">
              {pages && <Meter label="Pages" meter={pages} />}
              {papers && <Meter label="Papers this month" meter={papers} />}
            </div>
          )}
          <div className="account-menu__items">
            <button type="button" role="menuitem" onClick={go(onSubjects)}>
              Subjects
            </button>
            <button type="button" role="menuitem" onClick={go(onFeedback)}>
              Feedback
            </button>
            <button type="button" role="menuitem" onClick={go(onAccount)}>
              Account and plan
            </button>
            <button
              type="button"
              role="menuitem"
              className="account-menu__signout"
              onClick={go(onSignOut)}
            >
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
